"use client";

import { cn } from "@/lib";
import { routes } from "@/routes";
import { signOut } from "next-auth/react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BoUserCircle } from "solar-icon-react/bo";
import { useTranslations } from "next-intl";
import { useModalStore } from "@/store";
import { useRouter } from "next/navigation";
import WalletBalanceSummary from "./walletBalanceSummary";
import {
  BdBillList,
  BdBox,
  BdCalendar,
  BdLogout,
  BdPassword,
  BdVerifiedCheck,
  BdWallet,
} from "solar-icon-react/bd";

const UserDropdown = ({ phoneNumber }: { phoneNumber: string }) => {
  const t = useTranslations();
  const router = useRouter();
  const { setOpenModal } = useModalStore();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isBillsOpen, setIsBillsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  const updateDropdownPosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDropdownPosition({
      top: rect.bottom + 12,
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 224)),
    });
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isOpen]);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openDropdown = () => {
    clearCloseTimeout();
    updateDropdownPosition();
    setIsOpen(true);
  };

  const scheduleCloseDropdown = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsBillsOpen(false);
    }, 120);
  };
  const openChangePassModal = () => {
    setOpenModal("create-password-modal");
  };
  const handleLogout = () => {
    signOut({
      callbackUrl: routes.main.HOME.primaryPath,
    });
  };
  return (
    <div
      ref={triggerRef}
      className={cn("relative flex h-10 w-44 shrink-0! items-center gap-2")}
    >
      <div
        className="absolute inset-0 size-full h-15 cursor-pointer"
        onMouseEnter={openDropdown}
        onMouseLeave={scheduleCloseDropdown}
      >
        {/* Avatar & phone */}
        <div className={cn("flex items-center gap-2")}>
          <span className="text-md font-bold text-white xl:text-lg">
            {phoneNumber}
          </span>
          <BoUserCircle size={40} className="shrink-0! text-white!" />
        </div>

        {/* Dropdown */}
        {isOpen &&
          isMounted &&
          createPortal(
          <div
            className={cn(
              "fixed z-[1000] w-52 cursor-default! rounded-xl bg-gray-100 p-6 shadow-xl",
            )}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
            onMouseEnter={openDropdown}
            onMouseLeave={scheduleCloseDropdown}
          >
            {/* Triangle pointer */}
            <div
              className={cn(
                "absolute -top-2 left-6 h-4 w-4 rotate-45 bg-gray-100",
              )}
            />

            <WalletBalanceSummary />
            <ul className="flex flex-col gap-4 text-right">
              <li
                id="user-dropdown-wallet"
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdWallet className="text-primary-main!" size={20} />
                <Link
                  title={t("wallet.title")}
                  href={routes.main.WALLET.primaryPath}
                >
                  {t("wallet.title")}
                </Link>
              </li>
              <li
                id="user-dropdown-inquiry-history"
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdCalendar size={20} className="text-primary-main!" />
                <Link
                  title={t("header.inquiryHistory")}
                  href={routes.main.INQUIRY_HISTORY.primaryPath}
                >
                  {t("header.inquiryHistory")}
                </Link>
              </li>
              <li
                id="user-dropdown-packages"
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdBox size={20} className="text-primary-main!" />
                <Link
                  title={t("header.packages")}
                  href={routes.main.PACKAGES.primaryPath}
                >
                  {t("header.packages")}
                </Link>
              </li>
              <li
                id="user-dropdown-inquiry-validity"
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdVerifiedCheck size={20} className="text-primary-main!" />
                <Link
                  title={t("header.inquiryValidity")}
                  href={routes.main.INQUIRY_VALIDITY.primaryPath}
                >
                  {t("header.inquiryValidity")}
                </Link>
              </li>
              <li
                id="user-dropdown-paid-bills"
                className="flex flex-col gap-2"
              >
                <button
                  type="button"
                  onClick={() => setIsBillsOpen((prev) => !prev)}
                  className="hover:text-secondary-main flex cursor-pointer items-center gap-2 text-right transition-colors"
                >
                  <BdBillList size={20} className="text-primary-main!" />
                  <span>قبض‌ها</span>
                </button>
                {isBillsOpen && (
                  <div className="border-primary-main/15 mr-7 flex flex-col gap-2 border-r pr-3 text-sm">
                    <Link
                      title="قبض‌های من"
                      href={routes.main.MY_BILLS.primaryPath}
                      className="hover:text-secondary-main transition-colors"
                    >
                      قبض‌های من
                    </Link>
                    <Link
                      title="پرداخت سریع قبوض"
                      href={routes.main.QUICK_BILL_PAYMENT.primaryPath}
                      className="hover:text-secondary-main transition-colors"
                    >
                      پرداخت سریع قبوض
                    </Link>
                    <Link
                      title="قبض‌های پرداختی"
                      href={routes.main.BILL_HISTORY.primaryPath}
                      className="hover:text-secondary-main transition-colors"
                    >
                      قبض‌های پرداختی
                    </Link>
                  </div>
                )}
              </li>
              <li
                id="user-dropdown-change-password"
                onClick={openChangePassModal}
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdPassword size={20} className="text-primary-main!" />
                <span title={t("header.inquiryHistory")}>
                  {t("header.changePassword")}
                </span>
              </li>
              <li
                id="user-dropdown-logout"
                className="hover:text-secondary-main flex cursor-pointer items-center gap-2 transition-colors"
              >
                <BdLogout className="text-primary-main!" size={20} />
                <span onClick={handleLogout}>{t("auth.logout")}</span>
              </li>
            </ul>
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
};

export default UserDropdown;
